import { useState } from 'react';
import {
  Button, Card, Tab, TabGroup, TabList, TabPanel, TabPanels,
  Text, TextInput, Textarea, Title,
} from '@tremor/react';
import { t } from '../lib/i18n';
import type { User } from '../api/types';
import HelpPanel from './HelpPanel';
import IconButton from './IconButton';
import { CloseIcon } from './icons';
import Modal from './Modal';

/** A caller can pre-seed the ticket form (e.g. the Reports "Request a
 *  report" action). All fields optional. */
export type SupportPreset = { kind?: 'bug' | 'suggestion' | 'question'; title?: string; intro?: string };

interface Props {
  user: User;
  onClose: () => void;
  /** Which tab to land on. Defaults to "help" — most opens are operators
   *  looking for guidance; the ticket form is a deliberate secondary action. */
  defaultTab?: 'help' | 'support';
  /** Optional pre-seed for the ticket form. */
  preset?: SupportPreset;
}

const CSRF_COOKIE = 'pacebi_csrf';

function csrfToken(): string {
  const m = document.cookie.match(new RegExp('(?:^|; )' + CSRF_COOKIE + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : '';
}

// Last captured JS error, settable from the SupportButton's window listeners.
// Shared across renders so a ticket submitted *after* an error surfaces it.
let _lastJsError: string | null = null;
export function recordLastError(msg: string): void {
  _lastJsError = msg.slice(0, 500);
}

/**
 * Slide-in drawer from the right with two tabs:
 *   1. Ayuda — per-page Markdown bundled at build time (HelpPanel).
 *   2. Reportar problema — the ticket form.
 *
 * Both share the same chrome (header + close); the tab choice is a TabGroup
 * inside the same panel. Default tab is "help" so a confused user gets
 * answers before being asked to file a ticket.
 */
export default function SupportDrawer({ user, onClose, defaultTab = 'help', preset }: Props) {
  return (
    <Modal
      onClose={onClose}
      ariaLabel={t.support.drawerTitle}
      variant="drawer"
      panelClassName="flex flex-col"
    >
      <div className="p-5 border-b border-tremor-border flex items-start gap-3">
        <Title id="support-title">{t.support.drawerTitle}</Title>
        <IconButton label={t.common.close} onClick={onClose} className="ml-auto">
          <CloseIcon />
        </IconButton>
      </div>

      <TabGroup
        defaultIndex={defaultTab === 'support' ? 1 : 0}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabList className="px-5 pt-3" variant="solid">
          <Tab>{t.support.tabHelp}</Tab>
          <Tab>{t.support.tabSupport}</Tab>
        </TabList>
        <TabPanels className="flex-1 overflow-y-auto p-5">
          <TabPanel>
            <HelpPanel />
          </TabPanel>
          <TabPanel>
            <SupportForm user={user} onClose={onClose} preset={preset} />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Modal>
  );
}

type Kind = 'bug' | 'suggestion' | 'question';
type Ack = { id: string; linear?: { identifier: string; url: string } | null };

const KINDS: { value: Kind; label: () => string }[] = [
  { value: 'bug', label: () => t.support.kindBug },
  { value: 'suggestion', label: () => t.support.kindSuggestion },
  { value: 'question', label: () => t.support.kindQuestion },
];

function SupportForm({ user, onClose, preset }: { user: User; onClose: () => void; preset?: SupportPreset }) {
  const [title, setTitle] = useState(preset?.title ?? '');
  const [body, setBody]   = useState('');
  const [kind, setKind] = useState<Kind>(preset?.kind ?? 'bug');
  const [urgent, setUrgent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<Ack | null>(null);

  async function submit() {
    setErr(null);
    if (!title.trim() || !body.trim()) {
      setErr(t.support.errorRequired);
      return;
    }
    setSending(true);
    try {
      const ctx: Record<string, unknown> = {
        page: window.location.pathname,
        user_agent: navigator.userAgent,
      };
      if (_lastJsError) ctx.last_js_error = _lastJsError;

      const res = await fetch('/api/support/ticket', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ title: title.trim(), body: body.trim(), kind, urgent, context: ctx }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json() as Ack;
      setDone(data);
      // Auto-close after a beat so the user sees the confirmation without
      // having to dismiss manually — but only for the local-id fallback.
      // When the ack carries a Linear number the confirmation asks the user
      // to keep it, so that screen stays until they close it themselves.
      if (!data.linear) setTimeout(onClose, 4000);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <Card decoration="left" decorationColor="emerald">
        <Title>{t.support.thanksTitle}</Title>
        {done.linear ? (
          <>
            <Text className="mt-1">{t.support.thanksLinear}</Text>
            <Text className="text-lg font-mono font-semibold mt-1">{done.linear.identifier}</Text>
            <Text className="text-tremor-content text-xs mt-2 font-mono">{done.id}</Text>
          </>
        ) : (
          <>
            <Text className="mt-1">{t.support.thanksBody}</Text>
            <Text className="text-tremor-content text-xs mt-2 font-mono">{done.id}</Text>
            <Text className="text-tremor-content text-xs mt-1">{t.support.thanksPending}</Text>
          </>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {preset?.intro && (
        <Card decoration="left" decorationColor="blue">
          <Text className="text-sm">{preset.intro}</Text>
        </Card>
      )}
      <Text className="text-tremor-content text-sm">{t.support.subtitle}</Text>
      <fieldset>
        <legend className="text-tremor-content text-xs mb-1">{t.support.kindLabel}</legend>
        <div className="flex gap-3 flex-wrap">
          {KINDS.map((k) => (
            <label key={k.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name="support-kind"
                value={k.value}
                checked={kind === k.value}
                onChange={() => setKind(k.value)}
                disabled={sending}
              />
              {k.label()}
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <Text className="text-tremor-content text-xs mb-1">{t.support.fieldTitle}</Text>
        <TextInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t.support.titlePlaceholder}
          disabled={sending}
          maxLength={256}
        />
      </div>
      <div>
        <Text className="text-tremor-content text-xs mb-1">{t.support.fieldBody}</Text>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t.support.bodyPlaceholder}
          disabled={sending}
          rows={6}
          maxLength={4096}
        />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} disabled={sending} />
        {t.support.urgent}
      </label>

      <div className="text-tremor-content text-xs space-y-0.5 border-l-2 border-tremor-border pl-2">
        <div>{t.support.attached}</div>
        <div>• {t.support.attachedPage}: <span className="font-mono">{window.location.pathname}</span></div>
        <div>• {t.support.attachedUser}: <span className="font-mono">{user.username}</span></div>
        {_lastJsError && (
          <div>• {t.support.attachedError}: <span className="font-mono">{_lastJsError.slice(0, 60)}…</span></div>
        )}
      </div>

      {err && (
        <Card className="bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-900">
          <Text className="text-red-700 dark:text-red-300 text-sm">{err}</Text>
        </Card>
      )}

      <div className="flex gap-2">
        <Button
          onClick={submit}
          loading={sending}
          disabled={sending || !title.trim() || !body.trim()}
        >
          {t.support.submit}
        </Button>
        <Button variant="light" onClick={onClose} disabled={sending}>
          {t.common.close}
        </Button>
      </div>
    </div>
  );
}
